import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LocalStorageService } from 'ngx-webstorage';
import * as lodash from 'lodash';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, delay, distinctUntilChanged, filter, first, map, mergeMap, retryWhen, switchMap, tap } from 'rxjs/operators';

import { SYSTEM_ADMIN } from 'app/types/authority';
import { AccountService } from 'app/shared/auth';
import { createRequestOption } from '../model/request.utils';
import { NotificationService } from '../util/notification.service';



@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private readonly _organizations$ = new BehaviorSubject<Array<App.Organization>>(null);

  organizations$: Observable<App.Organization[]> = this._organizations$.asObservable()
    .pipe(
      filter((data): boolean => {
        return !!data;
      })
    );

  private resourceUrl = 'api/organizations';

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private notification: NotificationService,
    private accountService: AccountService
  ) {
  }

  init(): void {
    if (!this._organizations$.value) {
      this.reset();
    }
  }

  reset(): void {
    this.getAll().subscribe();
  }

  create(organization: App.Organization): Observable<App.Organization> {
    return this.http.post(this.organizationUrl(), organization).pipe(
      tap((value) => {
        this.updateOrganization(value);
      })
    );
  }

  update(organization: App.Organization): Observable<App.Organization> {
    return this.http.put<App.Organization>(this.organizationUrl(), organization).pipe(
      tap((value) => {
        this.updateOrganization(value);
      })
    );
  }

  find(orgName: string): Observable<App.Organization> {
    return this.organizations$.pipe(
      map(organizations => {
        return organizations.find(o => o.name === orgName);
      }),
      filter(o => !!o),
      distinctUntilChanged(lodash.isEqual),
    );
  }

  query(req?: any): Observable<App.GeneralPaginationResponse<App.Organization>> {
    const options = createRequestOption(req);

    return this.http.get<App.GeneralPaginationResponse<App.Organization>>(this.organizationUrl(), {
      params: options
    })
      .pipe(
        map((res) => {
          return {
            ...res,
            content: res.content.map((orgItem) => {
              return {
                ...orgItem,
                alias: `${orgItem.organizationId}-${orgItem.name}`,
                projects: orgItem.projects.map((porjectItem) => {
                  return {
                    ...porjectItem,
                    alias: `${porjectItem.projectId}-${porjectItem.projectName}`
                  };
                })
              };
            })
          };
        }),
      );
  }

  delete(orgName: string): Observable<any> {
    return this.http.delete(this.organizationUrl(orgName));
  }

  getAll(): Observable<Array<App.Organization>> {
    const accountService = this.accountService;

    return accountService.account$.pipe(
      first(),
      switchMap((account) => {
        if (account) {
          return this.http.get<App.GeneralPaginationResponse<App.Organization>>('api/all/organizations').pipe(
            retryWhen((errors) => {
              return errors.pipe(
                delay(1000),
                concatMap((error: HttpErrorResponse, count) => {
                  if (count <= 2 && (!error.status || error.status >= 500)) {
                    return of(error);
                  }
                  return throwError(error);
                })
              );
            }),
            catchError(() => {
              this.notification.openError($localize`:@@global.messages.getOrganizationsFail:Something went wrong when fetched the organizations.`);
              return of({
                content: [] as Array<App.Organization>,
                current: -1,
                total: 0
              } as App.GeneralPaginationResponse<App.Organization>);
            }),
            map((res) => {
              if (accountService.accountHasAnyAuthority(account, [SYSTEM_ADMIN])) {
                return res;
              }
              return {
                ...res,
                content: res.content.filter((orgItem): boolean => {
                  if (accountService.accountHasAnyAuthority(account, [orgItem])) {
                    return true;
                  }
                  orgItem.projects = orgItem.projects.filter((projectItem): boolean => {
                    return accountService.accountHasAnyAuthority(account, [projectItem]);
                  });
                  return !!orgItem.projects.length;
                })
              };
            })
          );
        } else {
          return of({
            content: [] as Array<App.Organization>,
            current: -1,
            total: 0
          });
        }
      }),
      map((res) => {
        return {
          ...res,
          content: res.content.map((orgItem) => {
            return {
              id: orgItem.id,
              organizationId: orgItem.organizationId,
              name: orgItem.name,
              alias: `${orgItem.organizationId}-${orgItem.name}`,
              status: orgItem.status,
              projects: orgItem.projects.map((porjectItem) => {
                return {
                  id: porjectItem.id,
                  projectId: porjectItem.projectId,
                  projectName: porjectItem.projectName,
                  alias: `${porjectItem.projectId}-${porjectItem.projectName}`,
                  softwareVersion: porjectItem.softwareVersion,
                  status: porjectItem.status
                } as App.Project;
              })
            } as App.Organization;
          })
        };
      }),
      distinctUntilChanged(lodash.isEqual),
      mergeMap((res) => {
        const orgs = res.content;

        return this.accountService.getSelectedOrganization()
          .pipe(
            first(),
            map((currOrg) => {
              if (!currOrg) {
                currOrg = this.localStorage.retrieve('currOrg');
              }
              if (currOrg) {
                currOrg = orgs.find((item) => {
                  return item && item.id === currOrg.id;
                });
              }
              if (!currOrg) {
                currOrg = orgs[0];
              }
              this.accountService.setSelectedOrganization(currOrg);
              return currOrg;
            }),
            mergeMap((currOrg) => {
              return this.accountService.getSelectedProject()
                .pipe(
                  first(),
                  map((currProject) => {
                    if (!currProject) {
                      currProject = this.localStorage.retrieve('currProject');
                    }
                    if (!!currOrg?.projects.length && currProject) {
                      currProject = currOrg.projects.find((item) => {
                        return item.id === currProject.id;
                      });
                    }
                    if (!!currOrg?.projects && (!currProject || (!currOrg.projects.some((item) => {
                      return item.id === currProject.id;
                    })))) {
                      currProject = currOrg.projects[0];
                    }
                    this.accountService.setSelectedProject(currProject);
                    return res;
                  })
                );
            })
          );
      }),
      map((res) => {
        this._organizations$.next(res.content);
        return res.content;
      })
    );
  }

  private updateOrganization(organization: App.Organization) {
    if (this._organizations$.value) {
      const nextValue = this._organizations$.value.slice();
      const idx = nextValue.findIndex(p => p.id === organization.id);

      if (idx >= 0) {
        nextValue[idx] = {
          ...nextValue[idx],
          ...organization,
        };
      } else {
        nextValue.push(organization);
      }
      this._organizations$.next(nextValue);
    }
  }

  protected organizationUrl(orgName?: string): string {
    if (orgName) {
      return this.resourceUrl + '/' + encodeURIComponent(orgName);
    } else {
      return this.resourceUrl;
    }
  }
}
